import React, { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import {
    getBrazeBannerCacheKey,
    getBrazeBannerHideKey,
    getCachedBrazeBanner,
    isBrazeSdkReady,
    subscribeToBrazeBannerUpdates,
    subscribeToBrazeReady,
} from 'apps/auth/analytics/braze';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { BannerCloseButton } from 'apps/auth/components/paysendPlus/styles';
import { BANNER_CLICKED, BANNER_CLOSE_CLICKED, BANNER_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { BANNER_NAME } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { logError } from 'apps/auth/helpers/clientLogger';

const BrazeBannerWrapper = styled.div`
    width: 100%;
    max-width: 504px;
    position: relative;
    margin: 0 auto;
    margin-top: ${(props) => props.$marginTop || 0};
    margin-bottom: ${(props) => props.$marginBottom || 0};
`;

const isBannerDismissalExpired = (bannerId) => {
    if (!bannerId || typeof localStorage === 'undefined') {
        return true;
    }
    const storedBanner = localStorage.getItem(getBrazeBannerHideKey(bannerId));
    if (!storedBanner) {
        return true;
    }
    const { showDate } = JSON.parse(storedBanner);
    return Date.now() >= showDate;
};

const getRecipientCountryMatch = (banner, recipientCountry) => {
    const property = banner?.properties?.recipient_country;
    if (typeof property === 'undefined') {
        return true;
    }
    if (property?.value === null || typeof property?.value !== 'string') {
        return false;
    }
    const segments = property.value.split(',');
    if (segments.length === 0) {
        return false;
    }
    const normalizedCountries = segments.map((country) => country.trim());
    if (normalizedCountries.some((country) => !/^[a-z]{2}$/i.test(country))) {
        return false;
    }
    if (!recipientCountry || !/^[a-z]{2}$/i.test(recipientCountry)) {
        return false;
    }
    return normalizedCountries.map((country) => country.toUpperCase()).includes(recipientCountry.toUpperCase());
};

const areSameBanner = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.id === b.id && JSON.stringify(a) === JSON.stringify(b);
};

const BrazeBannerPlacement = ({ placementId, recipientCountry, onVisibilityChange, dataTestId, marginTop, marginBottom }) => {
    const [banner, setBanner] = useState(() => getCachedBrazeBanner(placementId));

    useEffect(() => {
        // Re-sync on mount (handles SSR→hydration transition) and subscribe to future updates.
        setBanner(getCachedBrazeBanner(placementId));

        return subscribeToBrazeBannerUpdates((updatedPlacementIds) => {
            if (!updatedPlacementIds.includes(placementId)) {
                return;
            }
            const nextBanner = getCachedBrazeBanner(placementId);
            setBanner((current) => (areSameBanner(current, nextBanner) ? current : nextBanner));
        });
    }, [placementId]);

    const isVisible = useMemo(() => {
        if (!banner?.placementId) {
            return false;
        }
        return isBannerDismissalExpired(banner.id) && getRecipientCountryMatch(banner, recipientCountry);
    }, [banner, recipientCountry]);

    const canCloseBanner = banner?.properties?.show_close_button?.value !== false;

    useEffect(() => {
        onVisibilityChange?.(isVisible);
    }, [isVisible, onVisibilityChange]);

    useEffect(() => {
        if (isVisible) {
            amplitude.getInstance().logEvent(BANNER_SHOWN, { [BANNER_NAME]: placementId });
        }
    }, [banner, isVisible, placementId]);

    useEffect(() => {
        const insertCurrentBanner = () => {
            const container = document.getElementById(`braze-banner-${placementId}`);
            if (container && isVisible && typeof braze !== 'undefined' && braze.insertBanner && braze.isInitialized?.()) {
                braze.insertBanner(banner, container);
            }
        };
        insertCurrentBanner();
        if (isBrazeSdkReady()) {
            return undefined;
        }
        return subscribeToBrazeReady(insertCurrentBanner);
    }, [banner, isVisible, placementId]);

    const onCloseBannerClick = (e) => {
        e.stopPropagation();
        const rawShowAgain = banner?.properties?.show_again_after_days?.value;
        const days = parseInt(rawShowAgain, 10) || 30;
        amplitude.getInstance().logEvent(BANNER_CLOSE_CLICKED, { [BANNER_NAME]: placementId });
        setBanner(null);
        try {
            const showDate = Date.now() + days * 86400000;
            localStorage.setItem(getBrazeBannerHideKey(banner?.id), JSON.stringify({ showDate }));
            sessionStorage?.removeItem(getBrazeBannerCacheKey(placementId));
        } catch (err) {
            logError('Braze banner dismissal persistence failed:', err);
        }
    };
    if (!isVisible) {
        return null;
    }

    return (
        <BrazeBannerWrapper
            data-testid={dataTestId || `braze-banner-placement-${placementId}`}
            $marginTop={marginTop}
            $marginBottom={marginBottom}
            onClick={() => amplitude.getInstance().logEvent(BANNER_CLICKED, { [BANNER_NAME]: placementId })}
        >
            {canCloseBanner && (
                <BannerCloseButton>
                    <DiskButton size="small" iconName="cross" iconColor="white_base" type="button" variant="glass" onClick={onCloseBannerClick} />
                </BannerCloseButton>
            )}
            <div id={`braze-banner-${placementId}`} style={{ width: '100%' }} />
        </BrazeBannerWrapper>
    );
};

export default BrazeBannerPlacement;
